/**
 * Every URL that works today, and the React route it now lands on (R1, roadmap §4).
 *
 * This is an UPGRADE of a product a client already runs. Bookmarks, emailed activation
 * and invitation links, pass-links and external integrations all point at URLs that
 * were minted before this app existed, and every one of them must still land the user
 * on the right screen. URLs may be ADDED; none may be removed or repurposed.
 *
 * Two families are covered, and missing either one breaks live links:
 *
 *   1. The MVC URLs the client uses today   — `/Users`, `/General`, `/Invitations`…
 *   2. The current Features page routes     — `/admin/invitations`, `/admin/manageexams`…
 *      which already renamed some of the first family.
 *
 * MATCHING IS CASE-INSENSITIVE. ASP.NET routing is, so `/users`, `/Users` and `/USERS`
 * are all live URLs in the wild and all three have to resolve.
 *
 * The server side of the same contract is the SPA fallback in `Program.cs`: any
 * non-`/api`, non-static GET already serves this bundle, so a legacy URL never 404s —
 * it reaches here, and this turns it into the React route.
 */

/**
 * `legacy path -> react path`, or a function for the ones that carry data in the query
 * string. A function receives `URLSearchParams` and returns a path, or null to fall
 * through to the plain mapping.
 */
const ROUTES = {
  /* ---- entry ------------------------------------------------------- */
  /* NOT `'/': '/login'`. The root is not a legacy address that has moved — it is the
     app's own front door, and who is behind it depends on the session. Mapping it here
     redirected every visitor to the sign-in form before any route could look at the
     session, so a signed-in user was shown the login page and then bounced off it by
     `goToLanding()`, through a full page reload, to where `Landing` sends them directly.
     `/` now falls through to that route. */
  '/login': '/login',
  '/account': '/login',
  '/signup': '/signup',
  /* The reset link carries username and token in its query, which survives the map. */
  '/resetpassword': '/reset-password',
  '/install': '/install',
  '/error': '/error',
  '/unauthorized': '/unauthorized',
  '/shutdown': '/shutdown',

  /* `/Default` and `/ControlPanel` are NOT here on purpose: the server 302s them to
     the right screen per role, and the SPA then serves whatever that is. Mapping them
     client-side would replace a decision the server already makes. */

  /* `/Redirect` IS here — it is a page in its own right, not a 302. */
  '/redirect': '/redirect',

  /* ---- activation, reached from email ------------------------------ */
  /* The activation link confirms an address, and its query string IS the link.
     Mapped to itself so the casing is normalised and the query survives. */
  '/activate': '/activate',
  '/activate/index': '/activate',
  '/activate/waitinguser': '/activate/waiting-user',
  '/activate/waitingadmin': '/activate/waiting-admin',
  '/activate/expired': '/activate/expired',
  '/activate/resigned': '/activate/resigned',
  '/activate/suspended': '/activate/suspended',

  /* ---- statistics & license ---------------------------------------- */
  '/general': '/general',
  /* `General?type=fullscreen` renders `Views/General/Report.cshtml` — the dashboard with the
     shell taken off, for a projector or a printer. It used to land on the ordinary dashboard
     because React had nothing else to offer it; the page exists now, at the same address, so
     the bookmark arrives where it always meant to. */
  '/general/report': '/general/report',
  '/adminlicensemanagement': '/license',
  /* Two different screens, and they were crossed. `/admin/licensemanagement` is the
     SettingsManagers slice showing THIS tenant's own licence, so it belongs on /license;
     `/LicenseManagement` is the SystemManagers screen listing every company's. */
  '/admin/licensemanagement': '/license',
  '/licensemanagement': '/companies/license',
  '/licenseinformation': '/license-information',

  /* ---- users, groups, invitations ---------------------------------- */
  '/users': '/users',
  '/groups': '/groups',
  '/usersgroups': '/groups',
  '/invitations': '/invitations',
  '/admin/invitations': '/invitations',

  /* ---- learning content -------------------------------------------- */
  '/programsmanagement': '/programs',
  '/learningprogramsreports': '/programs-reports',
  '/categoriesmanagement': '/categories',
  '/admin/categories-management': '/categories',
  '/editquestions': '/questions',
  '/admin/edit-questions': '/questions',
  /* Now that there IS an exams screen, these land on it. They pointed at the programmes
     list while none existed, which is the wrong screen for an exams bookmark. */
  '/manageexams': '/exams',
  '/admin/manageexams': '/exams',
  /* Bare, with no user named, there is nothing better than the list — the legacy screen
     started with an empty username box. A link that DOES name one is rewritten below. */
  '/resetexamcounter': '/users',
  '/admin/resetexamcounter': '/users',
  '/certificatesettings': '/certificates',
  '/printcertificate': '/print-certificate',
  '/certificatevalidation': '/certificate-validation',

  /* ---- engagement --------------------------------------------------- */
  '/awarenessmanagement': '/awareness',
  '/admin/awareness-management': '/awareness',
  /* `-management`, not the shorter `/assessments` and `/surveys`, because those two are
     LIVE LEARNER URLs (see the portal block below) and R1 says an existing URL wins over
     a new one. The legacy already drew the same distinction — `/Assessments` was the
     learner's page and `/AssessmentsManagement` the admin's — so this keeps its naming. */
  '/assessmentsmanagement': '/assessments-management',
  '/admin/assessments-management': '/assessments-management',
  '/surveysmanagement': '/surveys-management',
  '/workspacepoliciessettings': '/policies',

  /* ---- phishing ------------------------------------------------------ */
  '/phishingtemplates': '/phishing/templates',
  '/admin/phishing-templates': '/phishing/templates',
  '/phishingcampaigns': '/phishing/campaigns',
  '/admin/phishing-campaigns': '/phishing/campaigns',
  '/phishingcategories': '/phishing/settings',
  '/admin/phishing-settings': '/phishing/settings',
  '/phishingdomains': '/phishing/settings',
  '/admin/phishing-domains': '/phishing/settings',

  /* ---- gamification --------------------------------------------------- */
  '/pointsactivities': '/gamification/settings',
  '/admin/points-activities': '/gamification/settings',
  '/pointsbadges': '/gamification/settings',
  '/admin/points-badges': '/gamification/settings',
  '/pointsrisklevels': '/gamification/settings',
  '/admin/pointsrisklevels': '/gamification/settings',
  '/pointsanalysis': '/gamification/analysis',
  '/admin/points-analysis': '/gamification/analysis',
  '/pointstransactions': '/gamification/transactions',
  '/admin/pointstransactions': '/gamification/transactions',
  '/pointsresetlogs': '/gamification/reset-logs',
  '/admin/pointsresetlogs': '/gamification/reset-logs',

  /* ---- reports, logs, organisations ------------------------------------ */
  '/securityreportsmanagement': '/security-reports',
  '/admin/security-reports-management': '/security-reports',
  '/systemstate': '/system-state',
  '/notificationsdetails': '/notifications-log',
  '/managecompanies': '/companies',

  /* ---- settings --------------------------------------------------------- */
  '/settings': '/settings',
  '/advancedsettings': '/settings/advanced',
  '/profilemapping': '/settings/advanced',
  '/settingskeys': '/settings/keys',
  /* The card that edits the terms document lives on the ADVANCED screen, beside the
     other things that change how people get in — not on the general settings page. */
  '/termsofusesettings': '/settings/advanced',

  /* ---- the learner portal ------------------------------------------------ */
  '/userdashboard': '/portal',
  '/courses': '/portal/courses',
  '/examhistory': '/portal/exam-history',
  '/awarenesses': '/portal/awareness',
  /* The learner's screens. These two keep the short path — see the note in the
     engagement block above. */
  '/assessments': '/portal/assessments',
  '/surveys': '/portal/surveys',
  '/workspacepolicies': '/portal/policies',
  '/securityreports': '/portal/reports',
  '/leaderboard': '/portal/leaderboard',
  '/userprofile': '/portal/profile',
  '/changepassword': '/portal/profile/password',
  '/contactus': '/portal/contact',
  '/usermanual': '/portal/manual',
  '/termsofuse': '/portal/terms',
  /* `ProgramCertificateController` carries no `[Route]`, so its live URL is the bare
     controller name. The screen exists here; only the row was missing, and without it a
     learner's saved link to their certificates fell through to `Landing`. */
  '/programcertificate': '/portal/certificates',
}

/**
 * The legacy routes that carry their subject in the query string. React puts it in the
 * path, so these rewrite rather than map — and each drops the parameter it consumed so
 * it does not linger in the new URL meaning nothing.
 */
/**
 * The same rewrite under both spellings of a path.
 *
 * `normalise` lowercases and trims a trailing slash and nothing else, so `/admin/x` and
 * `/x` are different keys. The API emits both for the same screen — `FilterUsers` links to
 * `/UserDetailsAdmin?username=`, the gamification lists to `/admin/userdetailsadmin?Id=` —
 * so a rewrite registered under one silently misses the other.
 */
const both = (path, rewrite) => ({ [path]: rewrite, [`/admin${path}`]: rewrite })

/** `?c=<id>` IS THE WHOLE BOOKMARK — a link to ONE campaign's report, not to the list. */
const phishingReportRewrite = (params) => {
  const id = params.get('c') ?? params.get('C')
  return id
    ? { path: `/phishing/report/${encodeURIComponent(id)}`, consumed: ['c', 'C'] }
    : { path: '/phishing/report' }
}

const REWRITES = {
  /* `General?type=fullscreen` — the dashboard with the shell taken off, for a projector or a
     printer. `GeneralController.Index(string type, ...)` renders `Views/General/Report.cshtml`
     for ANY non-empty `type`, which is why this tests the parameter rather than its value.

     The comment beside `'/general/report'` in the table above has always claimed the bookmark
     "arrives where it always meant to". It did not: nothing rewrote the legacy `?type=` form,
     so it matched the plain `'/general': '/general'` row and landed on the ordinary dashboard,
     inside the admin shell.

     Returning null with no `type` lets a bare `/General` fall through to that row, which is
     still the right destination for it. `f` and `t` (the report's date range) are deliberately
     NOT consumed — `/general/report` reads them. */
  ...both('/general', (params) => {
    const type = params.get('type') ?? params.get('Type')
    return type ? { path: '/general/report', consumed: ['type', 'Type'] } : null
  }),

  ...both('/userdetailsadmin', (params) => {
    const username = params.get('username') ?? params.get('Username')
    if (username) {
      return { path: `/users/${encodeURIComponent(username)}`, consumed: ['username', 'Username'] }
    }

    /* The other spelling the API emits, from the points and gamification lists. The React
       route takes either — `getUser` sends a Guid as `Id` and anything else as a name. */
    const id = params.get('Id') ?? params.get('id')
    return id ? { path: `/users/${encodeURIComponent(id)}`, consumed: ['Id', 'id'] } : null
  }),

  /* The legacy screen is POLICY-centric: a list of policies, and under it the users each
     was issued to. It went to `/users` here, which is the wrong half of the subject — a
     bookmark to it wants the policies screen, where those users now open in a drawer.
     A `username` is still honoured, because a link carrying one means that user. */
  /* Resetting a user's exam counter is the exam-tries tab of that user's screen: the same
     row, the same button, the same `UserExamTries.ExamCounter = 0`. See API_FINDINGS §36. */
  ...both('/resetexamcounter', (params) => {
    const username = params.get('username') ?? params.get('Username')
    return username
      ? { path: `/users/${encodeURIComponent(username)}`, consumed: ['username', 'Username'], hash: '#examTries' }
      : { path: '/users' }
  }),

  /* `?c=<id>` IS THE WHOLE BOOKMARK. This used to be a plain redirect to the campaigns
     list, which threw the campaign away — a link to one campaign's report landed on a list
     of all of them. The id now becomes the route. */
  /* `both('/phishing-report')`, NOT `both('/admin/phishing-report')`. `both` registers the
     path AND its `/admin` twin, so passing an already-prefixed path produced
     `/admin/phishing-report` and the impossible `/admin/admin/phishing-report`, while the
     bare MVC URL was never registered at all — and `/PhishingReport?c=<id>` is what
     `PhishingReportController.Index(int? c)` answers today, so that emailed link fell
     through to `Landing` and lost the campaign. */
  /* TWO SPELLINGS, and both are live. The MVC controller is `PhishingReportController`, so
     the URL the client runs today is `/PhishingReport` — one word, which lowercases to
     `/phishingreport`. This project's own route is `/admin/phishing-report`, hyphenated.
     `both()` covers a path and its `/admin` twin, so it is applied to the hyphenated form
     and the one-word MVC spelling is registered beside it. */
  ...both('/phishing-report', phishingReportRewrite),
  '/phishingreport': phishingReportRewrite,

  ...both('/userspoliciesdetails', (params) => {
    const username = params.get('username') ?? params.get('Username')
    return username
      ? { path: `/users/${encodeURIComponent(username)}`, consumed: ['username', 'Username'], hash: '#policies' }
      : { path: '/policies' }
  }),

  /* `?course=` FIRST, because that is the one the product actually emits —
     `Courses/Index.cshtml:318` and `ExamHistory/Index.cshtml:34` both build
     `CourseDetails?course={id}`. This read `id`/`Id`/`courseId` and none of those, so the
     rewrite could not have fired for a real legacy link even once the route stopped
     claiming the URL. The other three stay: they cost nothing and cover hand-made links. */
  '/coursedetails': (params) => {
    const id =
      params.get('course') ??
      params.get('Course') ??
      params.get('id') ??
      params.get('Id') ??
      params.get('courseId')
    return id
      ? {
          path: `/portal/courses/${encodeURIComponent(id)}`,
          consumed: ['course', 'Course', 'id', 'Id', 'courseId'],
        }
      : null
  },

  '/awarenessdetails': (params) => {
    const id = params.get('id') ?? params.get('Id')
    return id ? { path: `/portal/awareness/${encodeURIComponent(id)}`, consumed: ['id', 'Id'] } : null
  },

  /* One survey, opened directly. The legacy had no second URL for this — the list page read
     `?SurveyId=` on load and popped the questions modal over itself, and when
     `ViewSurveyInSeparateView` was on it re-entered the SAME path to render a full page instead.
     Both forms are this one link, and both land on the survey's own route here. */
  '/surveys': (params) => {
    const id = params.get('SurveyId') ?? params.get('surveyId')
    return id
      ? { path: `/portal/surveys/${encodeURIComponent(id)}`, consumed: ['SurveyId', 'surveyId'] }
      : null
  },

  /* The same V6 page as `/Assessments/Assessment/12` (see `resolveLegacyRoute`), with its id
     in the query instead — ASP.NET's default route accepts both. */
  '/assessments/assessment': (params) => {
    const id = params.get('id') ?? params.get('Id')
    return {
      path: id ? `/portal/assessments/${encodeURIComponent(id)}` : '/portal/assessments',
      consumed: ['id', 'Id'],
    }
  },

  '/companyprofile': (params) => {
    const id = params.get('companyId') ?? params.get('CompanyId')
    return id ? { path: `/companies/${encodeURIComponent(id)}/profile`, consumed: ['companyId', 'CompanyId'] } : null
  },

  /* These two carry DIFFERENT SUBJECTS under the same parameter name, and both used to land on
     `/portal/courses/{id}/exam` — so a legacy `/Exam?id=<guid>` put an ATTEMPT id in a COURSE
     slot. They are separated here.

     `/StartExam?id=` is a course: it opens the panel that begins an attempt.
     `/Exam?id=` is a userExamId: an attempt already in progress. */
  /* The exam history is per COURSE — the endpoint returns an empty list without one — so the
     legacy link's `courseid` is carried through under this app's own name for it. The flat entry
     above would have preserved the parameter but not renamed it, and the screen reads `course`.
     §72. */
  '/examhistory': (params) => {
    const id = params.get('courseid') ?? params.get('courseId')
    return id
      ? { path: `/portal/exam-history?course=${encodeURIComponent(id)}`, consumed: ['courseid', 'courseId'] }
      : null
  },

  '/startexam': (params) => {
    const id = params.get('courseId') ?? params.get('id')
    return id ? { path: `/portal/courses/${encodeURIComponent(id)}/exam`, consumed: ['courseId', 'id'] } : null
  },

  '/exam': (params) => {
    const id = params.get('id') ?? params.get('userExamId')
    if (id) return { path: `/portal/exam/${encodeURIComponent(id)}`, consumed: ['id', 'userExamId'] }

    /* Only the legacy start link uses `courseId` here, and it means "begin one for this course". */
    const courseId = params.get('courseId')
    return courseId
      ? { path: `/portal/courses/${encodeURIComponent(courseId)}/exam`, consumed: ['courseId'] }
      : null
  },
}

/**
 * Paths this app must never claim — they belong to the server, and a client-side
 * rewrite would swallow a file, an export or the realtime hub.
 *
 * `Program.cs` already excludes these from the SPA fallback; the list is repeated here
 * so a rewrite added later cannot reach past that exclusion.
 */
const NEVER_REWRITTEN = ['/api', '/health', '/messaging', '/root', '/assets']

/**
 * Resolves a location the browser arrived at into the React location it belongs to.
 *
 * @param {string} pathname
 * @param {string} [search]  the raw query string, `?a=b`
 * @param {string} [hash]
 * @returns {string|null} the full target (path + surviving query + hash), or null when
 *                        the path is already a React route and nothing should happen
 */
export function resolveLegacyRoute(pathname, search = '', hash = '') {
  const path = normalise(pathname)

  if (NEVER_REWRITTEN.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null
  }

  const params = new URLSearchParams(search)

  const rewrite = REWRITES[path]?.(params)
  if (rewrite) {
    rewrite.consumed?.forEach((key) => params.delete(key))
    return join(rewrite.path, params, rewrite.hash ?? hash)
  }

  /*
   * `/Courses/7` — a legacy URL that carries its subject in a PATH SEGMENT rather than a
   * query parameter, so the exact-match table above cannot express it.
   *
   * It narrows the programmes screen to one programme. React puts that in the query, because
   * `/portal/courses/7` already means COURSE seven — the two would collide on the same shape.
   */
  const programme = /^\/courses\/(\d+)$/.exec(path)
  if (programme) {
    params.set('program', programme[1])
    return join('/portal/courses', params, hash)
  }

  /* `/Assessments/Assessment/12` — the V6 page one assessment opens on, linked from the
     legacy mandatory-assessment notice. Its subject is a path segment, like `/Courses/7`. */
  const assessment = /^\/assessments\/assessment\/(\d+)$/.exec(path)
  if (assessment) return join(`/portal/assessments/${assessment[1]}`, params, hash)

  const mapped = ROUTES[path]
  // Already where it belongs — including every route this app owns and the legacy map
  // does not mention. Returning null is what stops a redirect loop.
  if (!mapped || mapped === pathname) return null

  return join(mapped, params, hash)
}

/** Trailing slashes and casing are both meaningless to ASP.NET routing. */
function normalise(pathname) {
  const lower = (pathname || '/').toLowerCase()
  return lower.length > 1 && lower.endsWith('/') ? lower.slice(0, -1) : lower
}

function join(path, params, hash) {
  const query = params.toString()
  return `${path}${query ? `?${query}` : ''}${hash ?? ''}`
}

/** Exported for the route-compatibility test, which walks every row. */
export const legacyRouteTable = ROUTES
export const legacyRewritePaths = Object.keys(REWRITES)
